import { useEffect, useState, useMemo } from "react";
import { useAudit } from "../../../contexts/auditContext";
import { useHookCache } from "../../../lib/hookCache";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { AuditSessionTabs } from "../../../components/audit/AuditSessionTabs";
import AuditPageHeader from "../../../components/audit/AuditPageHeader";
import { useWorkPaperTemplates } from "../../../hooks/useWorkPaperTemplates";
import { useTemplateFieldState } from "../../../hooks/useTemplateFieldState";
import { useGenerateFieldValues } from "../../../hooks/useGenerateFieldValues";
import { useAsyncWorkPaperDownload } from "../../../hooks/useAsyncWorkPaperDownload";
import { TemplateSelector } from "./components/TemplateSelector";
import { TemplateExportDisplay } from "./components/TemplateExportDisplay.tsx";
import { fetchFastApi } from "../../../api/fastapi";
import { toast } from "sonner";
import { ComputedTableValue } from "../../../models/workPaperTemplate";
import { workPaperTemplateApiBase } from "../../../lib/workPaperTemplateApi";
import { refusalDetail } from "../../../lib/refusalDetail";

export const AuditWorkPaperV2Page = () => {
  const { controlSession, sessionKind } = useAudit();
  const { invalidate } = useHookCache();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [settingDefault, setSettingDefault] = useState(false);
  const [updatingFieldId, setUpdatingFieldId] = useState<string | null>(null);
  const [updatingAttributeId, setUpdatingAttributeId] = useState<string | null>(
    null,
  );
  const [updatingComputedTableFieldId, setUpdatingComputedTableFieldId] =
    useState<string | null>(null);

  // Only offer templates matching this session's kind: the backend refuses to
  // assign a template of the other kind to this session. A session kind is a
  // template type.
  const { templates, loading: templatesLoading } = useWorkPaperTemplates({
    type: sessionKind,
  });
  const { startWorkPaperDownload, startBundleDownload, downloading } =
    useAsyncWorkPaperDownload();
  const {
    fieldStates,
    loading: fieldStatesLoading,
    refetch: refetchFieldStates,
    setFieldStates,
  } = useTemplateFieldState(controlSession?.id || null, selectedTemplateId, {
    fetchOnLoad: true,
  });
  const { generateValues, generating } = useGenerateFieldValues({
    sessionId: controlSession?.id || null,
    templateId: selectedTemplateId,
    onGenerated: refetchFieldStates,
  });

  // Set default template from control session if present. Only adopt it when
  // the filtered list offers it: a stored default of the other kind can't be
  // picked here, and loading its field state would only be refused.
  useEffect(() => {
    const storedTemplateId = controlSession?.wpTemplateId;
    if (!storedTemplateId || selectedTemplateId) {
      return;
    }
    if (templates.some((t) => t.id === storedTemplateId)) {
      setSelectedTemplateId(storedTemplateId);
    }
  }, [controlSession, selectedTemplateId, templates]);

  // Find the selected template details
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  const handleGenerateValues = async () => {
    if (!selectedTemplateId || !controlSession?.id) return;

    const success = await generateValues(controlSession.id, selectedTemplateId);
    if (success) {
      refetchFieldStates();
    }
  };

  const handleSetAsDefault = async () => {
    if (!selectedTemplateId || !controlSession?.id) return;

    setSettingDefault(true);
    try {
      // Both routers take the same {templateId} body and apply the same
      // eligibility rule (a base template, or one this workspace owns);
      // `sessionKind` picks which. Without the branch a work-step session
      // PATCHed the control route and 404'd, so its wp_template_id stayed NULL
      // and the xlsx export failed with "no work paper template is associated
      // with this session".
      await fetchFastApi({
        path:
          sessionKind === "work_step"
            ? `/work-steps/work-step-sessions/${controlSession.id}/template`
            : `/controls/v2/control-sessions/${controlSession.id}/template`,
        method: "PATCH",
        body: {
          templateId: selectedTemplateId,
        },
      });

      toast.success("Template set as default");

      // Refetch the control session envelope to pick up the updated wpTemplateId
      await invalidate(["control-session", controlSession.id]);
    } catch (error) {
      console.error("Failed to set default template:", error);
      toast.error(refusalDetail(error) ?? "Failed to set default template");
    } finally {
      setSettingDefault(false);
    }
  };

  const handleUpdateFieldValue = async (
    fieldId: string,
    newValue: string,
  ): Promise<void> => {
    if (!selectedTemplateId || !controlSession?.id) return;

    // Store previous state for rollback on error
    const previousFieldStates = fieldStates;

    // Optimistically update the UI
    setFieldStates((prevStates) =>
      prevStates.map((field) =>
        field.fieldId === fieldId
          ? {
              ...field,
              staticValue:
                field.fieldType === "static" ? newValue : field.staticValue,
              computedValue:
                field.fieldType === "dynamic" ? newValue : field.computedValue,
            }
          : field,
      ),
    );

    setUpdatingFieldId(fieldId);
    try {
      await fetchFastApi({
        path: `${workPaperTemplateApiBase(sessionKind, controlSession.id, selectedTemplateId)}/generated-values/${fieldId}`,
        method: "PATCH",
        body: {
          value: newValue,
        },
      });

      toast.success("Field value updated successfully");
    } catch (error) {
      console.error("Failed to update field value:", error);
      toast.error("Failed to update field value");

      // Rollback to previous state on error
      setFieldStates(previousFieldStates);

      throw error;
    } finally {
      setUpdatingFieldId(null);
    }
  };

  const handleUpdateAttributeDescription = async (
    attributeId: string,
    description: string,
  ): Promise<void> => {
    if (!controlSession?.id || !selectedTemplateId) return;

    // Store previous state for rollback on error
    const previousFieldStates = fieldStates;

    // Optimistically update the UI
    setFieldStates((prevStates) =>
      prevStates.map((field) => {
        if (field.fieldType !== "attribute_summary_table") return field;
        if (!field.attributeTableValue) return field;

        return {
          ...field,
          attributeTableValue: {
            ...field.attributeTableValue,
            rows: field.attributeTableValue.rows.map((row) =>
              row.attributeId === attributeId
                ? { ...row, attributeOutcomeDescription: description }
                : row,
            ),
          },
        };
      }),
    );

    setUpdatingAttributeId(attributeId);
    try {
      await fetchFastApi({
        path: `${workPaperTemplateApiBase(sessionKind, controlSession.id, selectedTemplateId)}/generated-attribute-descriptions/${attributeId}`,
        method: "PATCH",
        body: {
          description: description,
        },
      });

      toast.success("Attribute description updated successfully");
    } catch (error) {
      console.error("Failed to update attribute description:", error);
      toast.error("Failed to update attribute description");

      // Rollback to previous state on error
      setFieldStates(previousFieldStates);

      throw error;
    } finally {
      setUpdatingAttributeId(null);
    }
  };

  const handleUpdateComputedTableValue = async (
    fieldId: string,
    tableValue: ComputedTableValue,
  ): Promise<void> => {
    if (!selectedTemplateId || !controlSession?.id) return;

    // Store previous state for rollback on error
    const previousFieldStates = fieldStates;

    // Optimistically update the UI
    setFieldStates((prevStates) =>
      prevStates.map((field) =>
        field.fieldId === fieldId
          ? {
              ...field,
              computedTableValue: tableValue,
            }
          : field,
      ),
    );

    setUpdatingComputedTableFieldId(fieldId);
    try {
      await fetchFastApi({
        path: `${workPaperTemplateApiBase(sessionKind, controlSession.id, selectedTemplateId)}/generated-values/${fieldId}`,
        method: "PATCH",
        body: {
          value: tableValue,
        },
      });

      toast.success("Table value updated successfully");
    } catch (error) {
      console.error("Failed to update table value:", error);
      toast.error("Failed to update table value");

      // Rollback to previous state on error
      setFieldStates(previousFieldStates);

      throw error;
    } finally {
      setUpdatingComputedTableFieldId(null);
    }
  };

  const handleDownloadWorkPaper = () => {
    if (!controlSession?.id) {
      toast.error("No control session found");
      return;
    }

    startWorkPaperDownload(controlSession.id, selectedTemplateId);
  };

  const handleDownloadBundle = () => {
    if (!controlSession?.id) {
      toast.error("No control session found");
      return;
    }

    startBundleDownload(controlSession.id, selectedTemplateId);
  };

  // Gated on data, not `controlSessionLoading`: that flag is also set by a
  // background refetch of the envelope (Set as default, a finished Send to
  // Optro), which would otherwise unmount the whole tab behind a spinner.
  if (!controlSession) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-screen w-full max-w-[2500px] mx-auto flex flex-col">
      <div className="p-4">
        <AuditPageHeader extraPageLabel="Download Workpaper" />
        <div className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <AuditSessionTabs activeTab="workpaper-v2" v2={true} />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4">
        <div className="mt-6">
          <TemplateSelector
            templates={templates}
            templateType={sessionKind}
            selectedTemplateId={selectedTemplateId}
            onTemplateSelect={setSelectedTemplateId}
            loading={templatesLoading}
            defaultTemplateId={controlSession.wpTemplateId}
            onSetAsDefault={handleSetAsDefault}
            settingDefault={settingDefault}
          />

          {selectedTemplateId && selectedTemplate && (
            <div className="mt-6">
              <TemplateExportDisplay
                template={selectedTemplate}
                fieldStates={fieldStates}
                loading={fieldStatesLoading}
                onGenerate={handleGenerateValues}
                generating={generating}
                onUpdateFieldValue={handleUpdateFieldValue}
                updatingFieldId={updatingFieldId}
                onDownload={handleDownloadWorkPaper}
                onDownloadBundle={handleDownloadBundle}
                downloading={downloading}
                controlSession={controlSession}
                sessionKind={sessionKind}
                onUpdateAttributeDescription={handleUpdateAttributeDescription}
                updatingAttributeId={updatingAttributeId}
                onUpdateComputedTableValue={handleUpdateComputedTableValue}
                updatingComputedTableFieldId={updatingComputedTableFieldId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
